import { Button, Divider, Stack } from 'component-library';
import { Dropdown, DropdownContent, DropdownItemLink, DropdownTrigger } from 'component-library/Dropdown/Dropdown';
import { TextInput } from 'component-library/TextInput/TextInput';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SearchResult } from '@/api/rest/resources/types/search';
import { useDebounce } from '@/hooks/useDebounce';
import { usePathSet } from '@/hooks/usePathSet';
import { useIsLimitedAccessUser } from '@/pages/shared/hooks/useIsLimitedAccessUser';
import { useIsOrganizationMember } from '@/pages/shared/hooks/useIsOrganizationMember';
import { useOrganizationSearch } from '@/pages/shared/hooks/useOrganizationSearch';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';

import { Icon } from '../widgets/a/Icon/Icon';

type SearchResultItem = {
  id: string | null | undefined;
  name: string | null | undefined;
  path: string;
  icon: string;
};

type SearchSection = {
  label: string;
  items: SearchResultItem[];
};

const useSearchSections = (data: SearchResult | undefined): SearchSection[] => {
  const { t } = useTranslation();
  const pathSet = usePathSet();
  const sections = [];

  if (isNullish(data)) return [];

  if (data.plots.length > 0) {
    sections.push({
      label: `${t('navbar.plots')} (${data.plots.length})`,
      items: data.plots.map((plot) => ({
        id: plot.id,
        name: plot.name,
        icon: 'ri-map-pin-line',
        path: buildPath(pathSet.plot, {
          pathParams: {
            projectId: (plot.project_id ?? plot.project) as string,
            plotId: plot.id,
          },
        }),
      })),
    });
  }

  if (data.plot_groups.length > 0) {
    sections.push({
      label: `${t('navbar.groups')} (${data.plot_groups.length})`,
      items: data.plot_groups.map((group) => ({
        id: group.id,
        name: group.name,
        icon: 'ri-gallery-view-2',
        path: buildPath(pathSet.plotGroupPlots, {
          queryParams: {
            plotGroupId: group.id,
          },
        }),
      })),
    });
  }

  if (data.comparisons.length > 0) {
    sections.push({
      label: `${t('navbar.comparisons')} (${data.comparisons.length})`,
      items: data.comparisons.map((comparison) => ({
        id: comparison.id,
        name: comparison.name,
        icon: 'ri-layout-column-line',
        path: buildPath(pathSet.compare, {
          queryParams: {
            comparisonId: comparison.id,
          },
        }),
      })),
    });
  }

  if (data.reports.length > 0) {
    sections.push({
      label: `${t('navbar.reports')} (${data.reports.length})`,
      items: data.reports.map((report) => ({
        id: report.id,
        name: report.report_name ?? report.id,
        icon: 'ri-file-text-line',
        path: pathSet.reports,
      })),
    });
  }

  if (data.roadmaps.length > 0) {
    sections.push({
      label: `${t('navbar.goals')} (${data.roadmaps.length})`,
      items: data.roadmaps.map((roadmap) => ({
        id: roadmap.id,
        name: roadmap.title,
        icon: 'ri-compass-line',
        path: buildPath(pathSet.roadmap, {
          pathParams: { roadmapId: roadmap.id ?? '' },
        }),
      })),
    });
  }

  return sections;
};

export const OrganizationSearchBar = () => {
  const { t } = useTranslation();
  const isLimitedAccessUser = useIsLimitedAccessUser();
  const isOrganizationMember = useIsOrganizationMember();

  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);

  const debouncedInputText = useDebounce(inputValue, 800);

  const { data, isFetching } = useOrganizationSearch(
    undefined,
    debouncedInputText ? { query: debouncedInputText } : null,
  );

  const sections = useSearchSections(data);
  const hasResults = sections.length > 0;
  const noResultsFound = debouncedInputText.length > 0 && !isFetching && !hasResults;

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setOpen(!!inputValue);
    const timeout = setTimeout(() => inputValue && inputRef.current?.focus(), 0);
    return () => {
      clearTimeout(timeout);
    };
  }, [inputValue]);

  if (isLimitedAccessUser || !isOrganizationMember) return null;

  return (
    <div className='relative flex flex-1'>
      <Stack className='flex flex-1 h-11 flex-row gap-1 bg-neutral-black-4 border border-neutral-black-4 rounded-full justify-start px-4 py-1 items-center'>
        <Icon icon={isFetching ? 'ri-loader-4-line' : 'ri-search-line'} className='text-md' />
        <TextInput
          ref={inputRef}
          placeholder={t('shared.searchBar.placeholder')}
          className='w-full typography-body1 border-none bg-transparent h-full focus:border-none focus:outline-none focus-visible:outline-none px-0 py-0'
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
        />
        {inputValue && (
          <Button
            variant='text'
            className='text-text-secondary hover:text-text-primary px-1 py-1'
            onClick={() => setInputValue('')}
          >
            <Icon icon='ri-close-line' className='text-sm' />
          </Button>
        )}
      </Stack>

      <Dropdown
        open={open}
        onOpenChange={(newOpen) => {
          // prevent radix from closing dropdown when focus moves back to search bar
          // radix treats dropdown content as focused elements
          if (!newOpen && document.activeElement === inputRef.current) return;
          setOpen(newOpen);
        }}
      >
        <DropdownTrigger asChild>
          <div className='absolute inset-x-0 bottom-0 h-0 overflow-hidden' aria-hidden='true' />
        </DropdownTrigger>

        <DropdownContent
          side='bottom'
          align='start'
          sideOffset={8}
          className='w-[var(--radix-popper-anchor-width)] max-h-80 overflow-y-auto'
        >
          {noResultsFound && (
            <Stack className='px-4 py-3 typography-body2 text-text-secondary'>{t('shared.searchBar.noResults')}</Stack>
          )}
          {sections.map((section, index) => (
            <Stack key={section.label} className='py-1'>
              {index > 0 && <Divider className='pb-2' />}
              <Stack className='px-4 py-1.5 typography-overline text-text-secondary'>{section.label}</Stack>
              {section.items.map((item) => (
                <DropdownItemLink
                  key={item.id}
                  leftAdornment={<Icon icon={item.icon} />}
                  className='w-full truncate'
                  to={item.path}
                  onClick={() => {
                    setOpen(false);
                    setInputValue('');
                  }}
                >
                  {item.name ?? t('shared.searchBar.unknownResult')}
                </DropdownItemLink>
              ))}
            </Stack>
          ))}
        </DropdownContent>
      </Dropdown>
    </div>
  );
};
